import { Provider } from '@repo/api-gw-sdk';
import type { NextRequest } from 'next/server';

import { parseJson } from '@/utils/string';

export const providerAndMarketplaceTokenCookieName = 'mptoken';
export const sessionTokenCookieName = 'next-auth.session-token';
export const securedSessionTokenCookieName = `__Secure-${sessionTokenCookieName}`;
export const globalManagementAccessCookieName = 'eon-global-management-access';
export const demoSettingsCookieName = 'eon-demo-settings';
export const accountDomainCookieName = 'ead';
export const docsBaseUrl = 'https://docs.eon.io';

export const getGlobalManagementAccessCookieExpiration = () =>
  new Date(new Date().getTime() + 1000 * 60 * 60 * 2); // 2 hours
export const getDemoSettingsCookieExpiration = () =>
  new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 30); // 30 days

export const knownPublicDomains = [
  'console.eon.io',
  'console.app.eon.io',
  'testing-console.eon.io',
  'testing-console.app.eon.io',
  'dev-console.app.eon.io',
  'staging-console.eon.io',
];

export const getHostFromHeaders = (headerList: Pick<Headers, 'get'>): string =>
  headerList.get('x-forwarded-host') || headerList.get('host') || '';

export type LoginHostKind = 'public-root' | 'public-tenant' | 'custom';

export const getLoginHostKind = (host: string): LoginHostKind => {
  if (knownPublicDomains.includes(host)) {
    return 'public-root';
  }
  if (knownPublicDomains.some((domain) => host.endsWith(`.${domain}`))) {
    return 'public-tenant';
  }
  return 'custom';
};

export interface Environment {
  isDemo: boolean;
  isDev: boolean;
  isTest: boolean;
  enableForceLogoutOn401: boolean;
  clientApiBaseUrl: string;
  serviceAccountId: string;
  gcpControlPlane: string;
  gitCommit: string;
}

export interface DemoSettings {
  [Provider.Aws]: boolean;
  [Provider.Azure]: boolean;
  [Provider.Gcp]: boolean;
  [Provider.MongoAtlas]: boolean;
  [Provider.GoogleWorkspace]: boolean;
  [Provider.Microsoft365]: boolean;
  ai: boolean;
  logSpace: boolean;
  ransomware: boolean;
  integrations: boolean;
  lakehouse: boolean;
  dataKnowledgeGraph: boolean;
  primaryProvider: Provider.Aws | Provider.Azure | Provider.Gcp;
}

export const defaultDemoSettings: DemoSettings = {
  [Provider.Aws]: true,
  [Provider.Azure]: true,
  [Provider.Gcp]: true,
  [Provider.MongoAtlas]: true,
  [Provider.GoogleWorkspace]: false,
  [Provider.Microsoft365]: false,
  ai: false,
  logSpace: false,
  ransomware: false,
  integrations: false,
  lakehouse: false,
  dataKnowledgeGraph: false,
  primaryProvider: Provider.Aws,
};

export const defaultEnvironment: Environment = {
  isDemo: process.env.IS_DEMO === 'true',
  isDev: process.env.NODE_ENV === 'development',
  isTest: process.env.NODE_ENV === 'test' || process.env.IS_TESTING === 'true',
  enableForceLogoutOn401: process.env.ENABLE_FORCE_LOGOUT_ON_401 === 'true',
  clientApiBaseUrl: process.env.CLIENT_API_BASE_URL ?? '',
  serviceAccountId: process.env.SERVICE_ACCOUNT_ID || '',
  gcpControlPlane: process.env.GCP_CONTROL_PLANE || '',
  gitCommit: process.env.GIT_COMMIT || '',
};

export const useSecureCookies = !(
  defaultEnvironment.isDev ||
  defaultEnvironment.isTest ||
  process.env.ENVIRONMENT?.toLowerCase().includes('test')
);

export const getDemoSettings = (req: NextRequest) => {
  const cookie = req.cookies.get(demoSettingsCookieName);
  return getDemoSettingsFromCookie(cookie?.value);
};

export const getDemoSettingsFromCookie = (
  cookieValue: string | undefined | null
) => {
  if (cookieValue) {
    const demoSettings = parseJson<DemoSettings>(cookieValue);
    if (demoSettings) {
      return {
        ...defaultDemoSettings,
        ...demoSettings,
      };
    }
  }

  return defaultDemoSettings;
};
